import { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  useMediaQuery,
  Paper,
  Card,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { lighten, darken } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Warning as WarningIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";
import { MyFormData } from "./form-context";
import { useTranslation } from "react-i18next";
import {
  getProbabilityList,
  getSeverityPeople,
  getSeverityEnvironment,
  getSeverityAsset,
  getSeverityReputation,
  riskLevelColor,
} from "@/lib/listData";
import SquareMatrix from "./Matrix";

// Helper function to get risk level color
const getRiskLevelColor = (likelihood, severity) => {
  if (!likelihood || !severity) return null;
  return riskLevelColor[likelihood]?.[severity] || null;
};

// Helper function to get the maximum severity across all risk groups
const getMaxSeverity = (hazard) => {
  const severities = [
    hazard.residual_risk?.severity_people || hazard.severity_people || 1,
    hazard.residual_risk?.severity_asset || hazard.severity_asset || 1,
    hazard.residual_risk?.severity_environment ||
      hazard.severity_environment ||
      1,
    hazard.residual_risk?.severity_reputation ||
      hazard.severity_reputation ||
      1,
  ];
  return Math.max(...severities);
};

export default function RiskAssessmentNew() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { ptwData, setPtwData } = MyFormData();
  const [selectedHazard, setSelectedHazard] = useState(null);
  const [expandedAccordion, setExpandedAccordion] = useState(false);

  // Responsive design
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isDarkMode = theme.palette.mode === "dark";

  // Responsive scaling for matrix
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const matrixScale = isMobile ? 0.8 : isTablet ? 1 : 1.2;

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // Initialize with first hazard selected
  useState(() => {
    if (ptwData.hazards && ptwData.hazards.length > 0 && !selectedHazard) {
      setSelectedHazard(ptwData.hazards[0]);
    }
  });

  // Get the current hazard data from ptwData (always fresh)
  const currentHazard = selectedHazard
    ? ptwData.hazards.find((h) => h.id === selectedHazard.id)
    : null;

  // Handle precaution checkbox toggle
  const handleTogglePrecaution = (hazardId, precautionId) => {
    setPtwData((prev) => ({
      ...prev,
      hazards: prev.hazards.map((hazard) => {
        if (hazard.id === hazardId) {
          const precautionIds = hazard.precaution_ids || [];
          const isChecked = precautionIds.includes(precautionId);
          return {
            ...hazard,
            precaution_ids: isChecked
              ? precautionIds.filter((id) => id !== precautionId)
              : [...precautionIds, precautionId],
          };
        }
        return hazard;
      }),
    }));
  };

  // Handle likelihood change (can return to original, cannot go below original)
  const handleLikelihoodChange = (hazardId, value) => {
    setPtwData((prev) => ({
      ...prev,
      hazards: prev.hazards.map((hazard) => {
        if (hazard.id === hazardId) {
          const originalLikelihood = hazard.likelihood; // Original value from fetched data

          // Allow any value that is >= original value
          if (value >= originalLikelihood) {
            return {
              ...hazard,
              residual_risk: {
                ...hazard.residual_risk,
                likelihood: value,
              },
            };
          }
        }
        return hazard;
      }),
    }));
  };

  // Handle severity change (can return to original, cannot go below original)
  const handleSeverityChange = (hazardId, severityType, value) => {
    setPtwData((prev) => ({
      ...prev,
      hazards: prev.hazards.map((hazard) => {
        if (hazard.id === hazardId) {
          const originalSeverity = hazard[severityType]; // Original value from fetched data

          // Allow any value that is >= original value
          if (value >= originalSeverity) {
            return {
              ...hazard,
              residual_risk: {
                ...hazard.residual_risk,
                [severityType]: value,
              },
            };
          }
        }
        return hazard;
      }),
    }));
  };

  if (!ptwData.hazards || ptwData.hazards.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography color="text.secondary">
          {t("riskAssessment.noHazardsSelected")}
        </Typography>
      </Box>
    );
  }

  // Validation: Check if each hazard has at least one precaution
  const validationErrors = ptwData.hazards
    .map((hazard) => {
      if (!hazard.precaution_ids || hazard.precaution_ids.length === 0) {
        return `${hazard.name}: ${t("riskAssessment.noPrecautionSelected")}`;
      }
      return null;
    })
    .filter(Boolean);

  // Desktop/Tablet Layout
  if (!isSmallScreen) {
    return (
      <Box>
        {validationErrors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {t("riskAssessment.validationWarning")}
            </Typography>
            {validationErrors.map((error, idx) => (
              <Typography key={idx} variant="body2">
                • {error}
              </Typography>
            ))}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 3 }}>
          {/* Left side: Hazards List */}
          <Paper
            elevation={3}
            sx={{
              width: "420px",
              display: "flex",
              flexDirection: "column",
              borderRadius: 3,
              overflow: "hidden",
              backgroundColor: isDarkMode
                ? darken(theme.palette.background.paper, 0.02)
                : lighten(theme.palette.background.paper, 0.5),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
              },
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 3,
                py: 2,
                pt: 3,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              }}
            >
              <WarningIcon color="warning" />
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.warning.main,
                  fontWeight: 600,
                  flex: 1,
                }}
              >
                {t("riskAssessment.hazards")}
              </Typography>
              <Chip
                label={ptwData.hazards.length}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.warning.main, 0.15),
                  color: "warning.main",
                  fontWeight: 600,
                }}
              />
            </Box>

            {/* Hazards List */}
            <Box
              sx={{
                overflowY: "auto",
                p: 2,
                maxHeight: "550px",
              }}
            >
              <List sx={{ p: 0 }}>
                {ptwData.hazards.map((hazard, index) => {
                  const isSelected = selectedHazard?.id === hazard.id;
                  const hasNoPrecautions =
                    !hazard.precaution_ids ||
                    hazard.precaution_ids.length === 0;

                  return (
                    <ListItem
                      key={hazard.id}
                      onClick={() => setSelectedHazard(hazard)}
                      sx={{
                        mb: index < ptwData.hazards.length - 1 ? 0.75 : 0,
                        borderRadius: 2,
                        border: `2px solid ${isSelected ? theme.palette.warning.main : "transparent"}`,
                        bgcolor: isSelected
                          ? alpha(theme.palette.warning.main, 0.1)
                          : "transparent",
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: isSelected
                            ? alpha(theme.palette.warning.main, 0.15)
                            : alpha(theme.palette.warning.main, 0.05),
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {hasNoPrecautions ? (
                          <WarningIcon color="error" />
                        ) : (
                          <CheckCircleIcon color="success" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={hazard.name}
                        slotProps={{
                          primary: {
                            variant: "body2",
                            fontWeight: isSelected ? 600 : 500,
                            color: isSelected ? "warning.dark" : "text.primary",
                          },
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                px: 3,
                py: 1.5,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                bgcolor: alpha(theme.palette.background.paper, 0.5),
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {t("riskAssessment.selectHazardToAssess")}
              </Typography>
            </Box>
          </Paper>

          {/* Right side: Risk Assessment Details */}
          {currentHazard && (
            <Paper
              elevation={3}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                borderRadius: 3,
                overflow: "hidden",
                backgroundColor: isDarkMode
                  ? darken(theme.palette.background.paper, 0.02)
                  : lighten(theme.palette.background.paper, 0.5),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                },
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  px: 3,
                  py: 2,
                  pt: 3,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                }}
              >
                <AssessmentIcon color="primary" />
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    flex: 1,
                  }}
                >
                  {currentHazard.name}
                </Typography>
              </Box>

              {/* Content - Scrollable */}
              <Box
                sx={{
                  overflowY: "auto",
                  p: 3,
                  maxHeight: "550px",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {/* Precautions Section */}
                  <Card
                    elevation={0}
                    sx={{
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.05),
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <SecurityIcon color="success" />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, color: "success.main" }}
                        >
                          {t("riskAssessment.precautions")}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <List sx={{ p: 0 }}>
                        {currentHazard.precaution_categories &&
                        currentHazard.precaution_categories.length > 0 ? (
                          currentHazard.precaution_categories.map(
                            (category) => (
                              <Box key={category.id} sx={{ mb: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 700,
                                    color: alpha(
                                      theme.palette.primary.main,
                                      0.8
                                    ),
                                    mb: 1,
                                    pl: 1,
                                  }}
                                >
                                  {category.name} (
                                  {category.precautions?.length || 0})
                                </Typography>
                                <List sx={{ pl: 1, p: 0 }}>
                                  {category.precautions &&
                                  category.precautions.length > 0 ? (
                                    category.precautions.map((precaution) => {
                                      const isChecked =
                                        currentHazard.precaution_ids &&
                                        currentHazard.precaution_ids.includes(
                                          precaution.id
                                        );
                                      return (
                                        <ListItem
                                          key={precaution.id}
                                          sx={{
                                            py: 0.5,
                                            px: 1,
                                            transition: "all 0.2s ease",
                                            bgcolor: isChecked
                                              ? alpha(
                                                  theme.palette.success.main,
                                                  0.05
                                                )
                                              : "transparent",
                                            borderRadius: 1,
                                            "&:hover": {
                                              bgcolor: alpha(
                                                theme.palette.primary.main,
                                                0.04
                                              ),
                                            },
                                          }}
                                        >
                                          <ListItemIcon sx={{ minWidth: 40 }}>
                                            <Checkbox
                                              edge="start"
                                              checked={isChecked}
                                              onChange={() =>
                                                handleTogglePrecaution(
                                                  currentHazard.id,
                                                  precaution.id
                                                )
                                              }
                                            />
                                          </ListItemIcon>
                                          <ListItemText
                                            primary={precaution.name}
                                            slotProps={{
                                              primary: {
                                                variant: "body2",
                                              },
                                            }}
                                          />
                                        </ListItem>
                                      );
                                    })
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ pl: 1 }}
                                    >
                                      {t("riskAssessment.noPrecautions")}
                                    </Typography>
                                  )}
                                </List>
                              </Box>
                            )
                          )
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t("riskAssessment.noPrecautionCategories")}
                          </Typography>
                        )}
                      </List>
                    </Box>
                  </Card>

                  {/* Risk Controls Section */}
                  <Card
                    elevation={0}
                    sx={{
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.05),
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <AssessmentIcon color="warning" />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, color: "warning.main" }}
                        >
                          {t("riskAssessment.riskControls")}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {/* Likelihood */}
                        <FormControl fullWidth size="small">
                          <InputLabel>
                            {t("hazardPicker.likelihood")}
                          </InputLabel>
                          <Select
                            value={
                              currentHazard.residual_risk?.likelihood ||
                              currentHazard.likelihood ||
                              ""
                            }
                            onChange={(e) =>
                              handleLikelihoodChange(
                                currentHazard.id,
                                e.target.value
                              )
                            }
                            label={t("hazardPicker.likelihood")}
                          >
                            {getProbabilityList(t).map((level) => (
                              <MenuItem
                                key={level.value}
                                value={level.value}
                                disabled={
                                  level.value < currentHazard.likelihood
                                }
                              >
                                {level.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Severity People */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <FormControl fullWidth size="small">
                            <InputLabel>
                              {t("riskAssessment.severityPeople")}
                            </InputLabel>
                            <Select
                              value={
                                currentHazard.residual_risk?.severity_people ||
                                currentHazard.severity_people ||
                                ""
                              }
                              onChange={(e) =>
                                handleSeverityChange(
                                  currentHazard.id,
                                  "severity_people",
                                  e.target.value
                                )
                              }
                              label={t("riskAssessment.severityPeople")}
                            >
                              {getSeverityPeople(t).map((level) => (
                                <MenuItem
                                  key={level.value}
                                  value={level.value}
                                  disabled={
                                    level.value < currentHazard.severity_people
                                  }
                                >
                                  {level.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {currentHazard.residual_risk?.likelihood ||
                          currentHazard.likelihood ? (
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                backgroundColor:
                                  getRiskLevelColor(
                                    currentHazard.residual_risk?.likelihood ||
                                      currentHazard.likelihood,
                                    currentHazard.residual_risk
                                      ?.severity_people ||
                                      currentHazard.severity_people
                                  )?.backgroundColor || "transparent",
                                border: `1px solid ${
                                  getRiskLevelColor(
                                    currentHazard.residual_risk?.likelihood ||
                                      currentHazard.likelihood,
                                    currentHazard.residual_risk
                                      ?.severity_people ||
                                      currentHazard.severity_people
                                  )?.backgroundColor === "white"
                                    ? "#ccc"
                                    : "transparent"
                                }`,
                                flexShrink: 0,
                              }}
                            />
                          ) : null}
                        </Box>

                        {/* Severity Asset */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <FormControl fullWidth size="small">
                            <InputLabel>
                              {t("riskAssessment.severityAsset")}
                            </InputLabel>
                            <Select
                              value={
                                currentHazard.residual_risk?.severity_asset ||
                                currentHazard.severity_asset ||
                                ""
                              }
                              onChange={(e) =>
                                handleSeverityChange(
                                  currentHazard.id,
                                  "severity_asset",
                                  e.target.value
                                )
                              }
                              label={t("riskAssessment.severityAsset")}
                            >
                              {getSeverityAsset(t).map((level) => (
                                <MenuItem
                                  key={level.value}
                                  value={level.value}
                                  disabled={
                                    level.value < currentHazard.severity_asset
                                  }
                                >
                                  {level.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {currentHazard.residual_risk?.likelihood ||
                          currentHazard.likelihood ? (
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                backgroundColor:
                                  getRiskLevelColor(
                                    currentHazard.residual_risk?.likelihood ||
                                      currentHazard.likelihood,
                                    currentHazard.residual_risk
                                      ?.severity_asset ||
                                      currentHazard.severity_asset
                                  )?.backgroundColor || "transparent",
                                border: `1px solid ${
                                  getRiskLevelColor(
                                    currentHazard.residual_risk?.likelihood ||
                                      currentHazard.likelihood,
                                    currentHazard.residual_risk
                                      ?.severity_asset ||
                                      currentHazard.severity_asset
                                  )?.backgroundColor === "white"
                                    ? "#ccc"
                                    : "transparent"
                                }`,
                                flexShrink: 0,
                              }}
                            />
                          ) : null}
                        </Box>

                        {/* Severity Environment */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <FormControl fullWidth size="small">
                            <InputLabel>
                              {t("riskAssessment.severityEnvironment")}
                            </InputLabel>
                            <Select
                              value={
                                currentHazard.residual_risk
                                  ?.severity_environment ||
                                currentHazard.severity_environment ||
                                ""
                              }
                              onChange={(e) =>
                                handleSeverityChange(
                                  currentHazard.id,
                                  "severity_environment",
                                  e.target.value
                                )
                              }
                              label={t("riskAssessment.severityEnvironment")}
                            >
                              {getSeverityEnvironment(t).map((level) => (
                                <MenuItem
                                  key={level.value}
                                  value={level.value}
                                  disabled={
                                    level.value <
                                    currentHazard.severity_environment
                                  }
                                >
                                  {level.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {currentHazard.residual_risk?.likelihood ||
                          currentHazard.likelihood ? (
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                backgroundColor:
                                  getRiskLevelColor(
                                    currentHazard.residual_risk?.likelihood ||
                                      currentHazard.likelihood,
                                    currentHazard.residual_risk
                                      ?.severity_environment ||
                                      currentHazard.severity_environment
                                  )?.backgroundColor || "transparent",
                                border: `1px solid ${
                                  getRiskLevelColor(
                                    currentHazard.residual_risk?.likelihood ||
                                      currentHazard.likelihood,
                                    currentHazard.residual_risk
                                      ?.severity_environment ||
                                      currentHazard.severity_environment
                                  )?.backgroundColor === "white"
                                    ? "#ccc"
                                    : "transparent"
                                }`,
                                flexShrink: 0,
                              }}
                            />
                          ) : null}
                        </Box>

                        {/* Severity Reputation */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <FormControl fullWidth size="small">
                            <InputLabel>
                              {t("riskAssessment.severityReputation")}
                            </InputLabel>
                            <Select
                              value={
                                currentHazard.residual_risk
                                  ?.severity_reputation ||
                                currentHazard.severity_reputation ||
                                ""
                              }
                              onChange={(e) =>
                                handleSeverityChange(
                                  currentHazard.id,
                                  "severity_reputation",
                                  e.target.value
                                )
                              }
                              label={t("riskAssessment.severityReputation")}
                            >
                              {getSeverityReputation(t).map((level) => (
                                <MenuItem
                                  key={level.value}
                                  value={level.value}
                                  disabled={
                                    level.value <
                                    currentHazard.severity_reputation
                                  }
                                >
                                  {level.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {currentHazard.residual_risk?.likelihood ||
                          currentHazard.likelihood ? (
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                backgroundColor:
                                  getRiskLevelColor(
                                    currentHazard.residual_risk?.likelihood ||
                                      currentHazard.likelihood,
                                    currentHazard.residual_risk
                                      ?.severity_reputation ||
                                      currentHazard.severity_reputation
                                  )?.backgroundColor || "transparent",
                                border: `1px solid ${
                                  getRiskLevelColor(
                                    currentHazard.residual_risk?.likelihood ||
                                      currentHazard.likelihood,
                                    currentHazard.residual_risk
                                      ?.severity_reputation ||
                                      currentHazard.severity_reputation
                                  )?.backgroundColor === "white"
                                    ? "#ccc"
                                    : "transparent"
                                }`,
                                flexShrink: 0,
                              }}
                            />
                          ) : null}
                        </Box>
                      </Box>
                    </Box>
                  </Card>

                  {/* Risk Matrix */}
                  <Card
                    elevation={0}
                    sx={{
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.05),
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <AssessmentIcon color="info" />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, color: "info.main" }}
                        >
                          {t("squareMatrix.title")}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        p: 3,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        bgcolor: alpha(theme.palette.info.main, 0.02),
                      }}
                    >
                      <Box
                        sx={{
                          transform: `scale(${matrixScale})`,
                          transformOrigin: "center",
                        }}
                      >
                        <SquareMatrix
                          receivedSeverity={getMaxSeverity(currentHazard)}
                          receivedProbability={
                            currentHazard.residual_risk?.likelihood ||
                            currentHazard.likelihood ||
                            1
                          }
                        />
                      </Box>
                    </Box>
                  </Card>
                </Box>
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  px: 3,
                  py: 1.5,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {t("riskAssessment.assessRiskAndPrecautions")}
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>
      </Box>
    );
  }

  // Mobile/Small Screen Layout - Accordion Based
  return (
    <Box>
      {validationErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {t("riskAssessment.validationWarning")}
          </Typography>
          {validationErrors.map((error, idx) => (
            <Typography key={idx} variant="body2">
              • {error}
            </Typography>
          ))}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {ptwData.hazards.map((hazard) => (
          <Accordion
            key={hazard.id}
            expanded={expandedAccordion === hazard.id}
            onChange={handleAccordionChange(hazard.id)}
            sx={{
              mb: 1,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              transition: "all 0.3s ease",
              "&:hover": {
                borderColor: "warning.main",
                boxShadow: theme.shadows[4],
              },
              "&:before": {
                display: "none",
              },
              "&.Mui-expanded": {
                margin: "0 0 4px 0",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                borderRadius: 2,
                "&.Mui-expanded": {
                  borderRadius: "8px 8px 0 0",
                },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  flex: 1,
                }}
              >
                {hazard.name}
              </Typography>
              <Box
                sx={{ display: "flex", gap: 1, alignItems: "center", mr: 2 }}
              >
                {(!hazard.precaution_ids ||
                  hazard.precaution_ids.length === 0) && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.error.main, fontWeight: 600 }}
                  >
                    ⚠ {t("riskAssessment.noPrecautionSelected")}
                  </Typography>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Left side: Precautions and Risk Controls */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    {/* Precautions Section */}
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          color: theme.palette.primary.main,
                        }}
                      >
                        {t("riskAssessment.precautions")}
                      </Typography>
                      <List sx={{ p: 0 }}>
                        {hazard.precaution_categories &&
                        hazard.precaution_categories.length > 0 ? (
                          hazard.precaution_categories.map((category) => (
                            <Box key={category.id} sx={{ mb: 2 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  color: alpha(theme.palette.primary.main, 0.8),
                                  display: "block",
                                  mb: 1,
                                  pl: 1,
                                }}
                              >
                                {category.name} (
                                {category.precautions?.length || 0})
                              </Typography>
                              <List sx={{ pl: 2, p: 1 }}>
                                {category.precautions &&
                                category.precautions.length > 0 ? (
                                  category.precautions.map((precaution) => {
                                    const isChecked =
                                      hazard.precaution_ids &&
                                      hazard.precaution_ids.includes(
                                        precaution.id
                                      );
                                    return (
                                      <ListItem
                                        key={precaution.id}
                                        sx={{
                                          py: 0.5,
                                          px: 1,
                                          transition: "all 0.2s ease",
                                          bgcolor: isChecked
                                            ? alpha(
                                                theme.palette.success.main,
                                                0.05
                                              )
                                            : "transparent",
                                          "&:hover": {
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              0.04
                                            ),
                                          },
                                        }}
                                      >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                          <Checkbox
                                            edge="start"
                                            checked={isChecked}
                                            onChange={() =>
                                              handleTogglePrecaution(
                                                hazard.id,
                                                precaution.id
                                              )
                                            }
                                          />
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={precaution.name}
                                          primaryTypographyProps={{
                                            variant: "body2",
                                          }}
                                        />
                                      </ListItem>
                                    );
                                  })
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ pl: 1 }}
                                  >
                                    {t("riskAssessment.noPrecautions")}
                                  </Typography>
                                )}
                              </List>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t("riskAssessment.noPrecautionCategories")}
                          </Typography>
                        )}
                      </List>
                    </Box>

                    {/* Risk Controls Section - Stacked Vertically */}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          color: theme.palette.primary.main,
                        }}
                      >
                        {t("riskAssessment.riskControls")} -{" "}
                        {t("squareMatrix.title")}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {/* Likelihood */}
                        <FormControl fullWidth size="small">
                          <InputLabel>
                            {t("hazardPicker.likelihood")}
                          </InputLabel>
                          <Select
                            value={
                              hazard.residual_risk?.likelihood ||
                              hazard.likelihood ||
                              ""
                            }
                            onChange={(e) =>
                              handleLikelihoodChange(hazard.id, e.target.value)
                            }
                            label={t("hazardPicker.likelihood")}
                          >
                            {getProbabilityList(t).map((level) => (
                              <MenuItem
                                key={level.value}
                                value={level.value}
                                disabled={level.value < hazard.likelihood}
                              >
                                {level.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Severity People */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <FormControl fullWidth size="small">
                            <InputLabel>
                              {t("riskAssessment.severityPeople")}
                            </InputLabel>
                            <Select
                              value={
                                hazard.residual_risk?.severity_people ||
                                hazard.severity_people ||
                                ""
                              }
                              onChange={(e) =>
                                handleSeverityChange(
                                  hazard.id,
                                  "severity_people",
                                  e.target.value
                                )
                              }
                              label={t("riskAssessment.severityPeople")}
                            >
                              {getSeverityPeople(t).map((level) => (
                                <MenuItem
                                  key={level.value}
                                  value={level.value}
                                  disabled={
                                    level.value < hazard.severity_people
                                  }
                                >
                                  {level.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {hazard.residual_risk?.likelihood ||
                          hazard.likelihood ? (
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                backgroundColor:
                                  getRiskLevelColor(
                                    hazard.residual_risk?.likelihood ||
                                      hazard.likelihood,
                                    hazard.residual_risk?.severity_people ||
                                      hazard.severity_people
                                  )?.backgroundColor || "transparent",
                                border: `1px solid ${
                                  getRiskLevelColor(
                                    hazard.residual_risk?.likelihood ||
                                      hazard.likelihood,
                                    hazard.residual_risk?.severity_people ||
                                      hazard.severity_people
                                  )?.backgroundColor === "white"
                                    ? "#ccc"
                                    : "transparent"
                                }`,
                                flexShrink: 0,
                              }}
                            />
                          ) : null}
                        </Box>

                        {/* Severity Asset */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <FormControl fullWidth size="small">
                            <InputLabel>
                              {t("riskAssessment.severityAsset")}
                            </InputLabel>
                            <Select
                              value={
                                hazard.residual_risk?.severity_asset ||
                                hazard.severity_asset ||
                                ""
                              }
                              onChange={(e) =>
                                handleSeverityChange(
                                  hazard.id,
                                  "severity_asset",
                                  e.target.value
                                )
                              }
                              label={t("riskAssessment.severityAsset")}
                            >
                              {getSeverityAsset(t).map((level) => (
                                <MenuItem
                                  key={level.value}
                                  value={level.value}
                                  disabled={level.value < hazard.severity_asset}
                                >
                                  {level.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {hazard.residual_risk?.likelihood ||
                          hazard.likelihood ? (
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                backgroundColor:
                                  getRiskLevelColor(
                                    hazard.residual_risk?.likelihood ||
                                      hazard.likelihood,
                                    hazard.residual_risk?.severity_asset ||
                                      hazard.severity_asset
                                  )?.backgroundColor || "transparent",
                                border: `1px solid ${
                                  getRiskLevelColor(
                                    hazard.residual_risk?.likelihood ||
                                      hazard.likelihood,
                                    hazard.residual_risk?.severity_asset ||
                                      hazard.severity_asset
                                  )?.backgroundColor === "white"
                                    ? "#ccc"
                                    : "transparent"
                                }`,
                                flexShrink: 0,
                              }}
                            />
                          ) : null}
                        </Box>

                        {/* Severity Environment */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <FormControl fullWidth size="small">
                            <InputLabel>
                              {t("riskAssessment.severityEnvironment")}
                            </InputLabel>
                            <Select
                              value={
                                hazard.residual_risk?.severity_environment ||
                                hazard.severity_environment ||
                                ""
                              }
                              onChange={(e) =>
                                handleSeverityChange(
                                  hazard.id,
                                  "severity_environment",
                                  e.target.value
                                )
                              }
                              label={t("riskAssessment.severityEnvironment")}
                            >
                              {getSeverityEnvironment(t).map((level) => (
                                <MenuItem
                                  key={level.value}
                                  value={level.value}
                                  disabled={
                                    level.value < hazard.severity_environment
                                  }
                                >
                                  {level.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {hazard.residual_risk?.likelihood ||
                          hazard.likelihood ? (
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                backgroundColor:
                                  getRiskLevelColor(
                                    hazard.residual_risk?.likelihood ||
                                      hazard.likelihood,
                                    hazard.residual_risk
                                      ?.severity_environment ||
                                      hazard.severity_environment
                                  )?.backgroundColor || "transparent",
                                border: `1px solid ${
                                  getRiskLevelColor(
                                    hazard.residual_risk?.likelihood ||
                                      hazard.likelihood,
                                    hazard.residual_risk
                                      ?.severity_environment ||
                                      hazard.severity_environment
                                  )?.backgroundColor === "white"
                                    ? "#ccc"
                                    : "transparent"
                                }`,
                                flexShrink: 0,
                              }}
                            />
                          ) : null}
                        </Box>

                        {/* Severity Reputation */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <FormControl fullWidth size="small">
                            <InputLabel>
                              {t("riskAssessment.severityReputation")}
                            </InputLabel>
                            <Select
                              value={
                                hazard.residual_risk?.severity_reputation ||
                                hazard.severity_reputation ||
                                ""
                              }
                              onChange={(e) =>
                                handleSeverityChange(
                                  hazard.id,
                                  "severity_reputation",
                                  e.target.value
                                )
                              }
                              label={t("riskAssessment.severityReputation")}
                            >
                              {getSeverityReputation(t).map((level) => (
                                <MenuItem
                                  key={level.value}
                                  value={level.value}
                                  disabled={
                                    level.value < hazard.severity_reputation
                                  }
                                >
                                  {level.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {hazard.residual_risk?.likelihood ||
                          hazard.likelihood ? (
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                backgroundColor:
                                  getRiskLevelColor(
                                    hazard.residual_risk?.likelihood ||
                                      hazard.likelihood,
                                    hazard.residual_risk?.severity_reputation ||
                                      hazard.severity_reputation
                                  )?.backgroundColor || "transparent",
                                border: `1px solid ${
                                  getRiskLevelColor(
                                    hazard.residual_risk?.likelihood ||
                                      hazard.likelihood,
                                    hazard.residual_risk?.severity_reputation ||
                                      hazard.severity_reputation
                                  )?.backgroundColor === "white"
                                    ? "#ccc"
                                    : "transparent"
                                }`,
                                flexShrink: 0,
                              }}
                            />
                          ) : null}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                {/* Right side: 5x5 Risk Matrix */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      p: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.02),
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        transform: `scale(${matrixScale})`,
                        transformOrigin: "center",
                      }}
                    >
                      <SquareMatrix
                        receivedSeverity={getMaxSeverity(hazard)}
                        receivedProbability={
                          hazard.residual_risk?.likelihood ||
                          hazard.likelihood ||
                          1
                        }
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
}
